import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';

import BayFilesUpload from '../../../../BayFilesUpload';
import { getAllowedFileTypes } from '../../../../utilities/constants/mimeTypes';
import { urlRegex } from '../../../../utilities/regex';
import '../../EditWidget/EditWidget.scss';

interface ValidationState {
	isValid: boolean;
	message: string;
}

interface EditBannerProps {
	t: (key: string, params?: any) => string;
	editModalInputFields?: any;
	setEditModalInputFields: (fields: any) => void;
	widgetId: string;
	setBannerImageUrl: (file: File | null) => void;
}

const EditBanner: React.FC<EditBannerProps> = ({
	t,
	editModalInputFields,
	setEditModalInputFields,
	widgetId,
	setBannerImageUrl,
}) => {
	const allowedFileTypes = ['jpeg', 'jpg', 'png'];
	const [urlValidation, setUrlValidation] = useState<ValidationState>({
		isValid: true,
		message: '',
	});

	const onDrop = (acceptedFiles: File[]) => {
		if (acceptedFiles?.length > 0) {
			const file = acceptedFiles[0];
			const url = URL.createObjectURL(file);
			setBannerImageUrl(file);
			setEditModalInputFields((prevFields: any) => ({
				...prevFields,
				[widgetId]: {
					...prevFields[widgetId],
					bannerImage: url,
				},
			}));
		}
	};

	const { getInputProps, open } = useDropzone({
		noClick: true,
		accept: getAllowedFileTypes(allowedFileTypes).mimeTypes,
		onDrop,
	});

	const validateUrl = (url: string): boolean => {
		if (!url?.trim()?.length) {
			setUrlValidation({ isValid: false, message: t('thisFieldIsRequired') });
			return false;
		}

		const trimmedUrl = url.trim();
		// Allow URLs with or without protocol, but check basic format
		const urlWithProtocol =
			trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')
				? trimmedUrl
				: `https://${trimmedUrl}`;

		if (!urlRegex.test(urlWithProtocol)) {
			setUrlValidation({
				isValid: false,
				message: t('invalidUrlNote') || 'Please enter a valid URL',
			});
			return false;
		}

		setUrlValidation({ isValid: true, message: '' });
		return true;
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		// Clear validation state when user types in URL field
		if (name === 'bannerHoverUrl') {
			setUrlValidation({ isValid: true, message: '' });
		}

		setEditModalInputFields((prevFields: any) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				[name]: value,
			},
		}));
	};

	const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const url = e.target.value;
		if (url) {
			validateUrl(url);
		}
	};

	return (
		<div className="edit-widget-modal-content">
			<Form.Group key="headerImage" className="mb-3">
				<Form.Label>
					{t('layerTypes.image')}
					<span className="text-danger">*</span>
				</Form.Label>
			</Form.Group>
			{editModalInputFields?.[widgetId]?.bannerImage ? (
				<div className="d-flex flex-column gap-2">
					<img
						alt="cropped"
						className="edit-banner-image"
						crossOrigin="anonymous"
						loading="lazy"

						src={editModalInputFields?.[widgetId]?.bannerImage}
					/>
					<input {...getInputProps()} />
					<span
						className="text-primary fw-normal fs-6 cursor-pointer mb-3"
						onClick={open}
					>
						{t('replacePhoto')}
					</span>
				</div>
			) : (
				<BayFilesUpload
					{...{
						allowedFileTypes:
							getAllowedFileTypes(allowedFileTypes).mimeTypes ||
							(allowedFileTypes as any),
						allowedFileTypeslabel: t('allowedImageTypeLabel'),
						allowedFileTypesText: `${t('allowedImageType')}\n${t('allowedImageTypeText', { width: 240, height: 80 })}` as any,
						className: 'mb-3',
						noData: true,
						uploadLinkText: t('uploadImageLinkText'),
						onDrop,
					} as any}
				/>
			)}
			<Form.Group className="mb-3">
				<div
					className={`position-relative ${
						!editModalInputFields?.[widgetId]?.bannerHoverText ||
						editModalInputFields?.[widgetId]?.bannerHoverText?.length > 20
							? 'border-danger'
							: ''
					}`}
				>
					<Form.Label>
						{t('hoverButtonText')}
						<span className="text-danger">*</span>
					</Form.Label>
					<Form.Control
						className={`${
							!editModalInputFields?.[widgetId]?.bannerHoverText ||
							editModalInputFields?.[widgetId]?.bannerHoverText?.length > 20
								? 'is-invalid'
								: ''
						} pe-7`}
						name="bannerHoverText"
						placeholder="Enter..."
						type="text"
						value={editModalInputFields?.[widgetId]?.bannerHoverText}
						onChange={handleInputChange}
					/>
					<span
						className={`edit-banner-counter position-absolute end-0 pe-2 small fw-bold ${
							!editModalInputFields?.[widgetId]?.bannerHoverText ||
							editModalInputFields?.[widgetId]?.bannerHoverText?.length > 20
								? 'text-danger'
								: 'text-muted'
						}`}
					>
						{editModalInputFields?.[widgetId]?.bannerHoverText?.length}/{20}
					</span>
				</div>
				{!editModalInputFields?.[widgetId]?.bannerHoverText && (
					<div className="text-danger small mt-1">
						{t('thisFieldIsRequired')}
					</div>
				)}
				{editModalInputFields?.[widgetId]?.bannerHoverText?.length > 20 && (
					<div className="text-danger small mt-1">
						{t('mustBeLessThanNCharacters', { n: 20 })}
					</div>
				)}
			</Form.Group>
			<Form.Group className="mb-3">
				<div
					className={`position-relative ${!editModalInputFields?.[widgetId]?.bannerHoverUrl || !urlValidation.isValid ? 'border-danger' : ''}`}
				>
					<Form.Label className="d-flex justify-content-between">
						<div>
							Hover Button URL
							<span className="text-danger">*</span>
						</div>
						{urlValidation.isValid &&
						editModalInputFields?.[widgetId]?.bannerHoverUrl ? (
								<a
									className="text-primary fw-normal"
									href={
										editModalInputFields?.[widgetId]?.bannerHoverUrl.startsWith(
											'http',
										)
											? editModalInputFields?.[widgetId]?.bannerHoverUrl
											: `https://${editModalInputFields?.[widgetId]?.bannerHoverUrl}`
									}
									rel="noopener noreferrer"
									target="_blank"
								>
									{t('testThisLink') || 'Test this link'}
								</a>
							) : (
								<span className="text-muted fw-normal">
									{t('testThisLink') || 'Test this link'}
								</span>
							)}
					</Form.Label>
					<Form.Control
						className={`${!editModalInputFields?.[widgetId]?.bannerHoverUrl || !urlValidation.isValid ? 'is-invalid' : ''} pe-7`}
						name="bannerHoverUrl"
						placeholder="example.com"
						type="text"
						value={editModalInputFields?.[widgetId]?.bannerHoverUrl}
						onBlur={handleUrlBlur}
						onChange={handleInputChange}
					/>
				</div>
				{!editModalInputFields?.[widgetId]?.bannerHoverUrl && (
					<span className="text-danger small mt-1">
						{t('thisFieldIsRequired')}
					</span>
				)}
				{!urlValidation.isValid && urlValidation.message && (
					<span className="text-danger small mt-1">
						{urlValidation.message}
					</span>
				)}
			</Form.Group>
		</div>
	);
};

export default EditBanner;
